/**
 * Designation detail panel — the "detail" side of the Finance tab's
 * master-detail layout.
 *
 * Inline ~380px panel on desktop, full-width right Drawer below `md`.
 * Field editing is delegated to the container's `renderCell` (same
 * click-to-edit + immediate `updateDoc` flow as the old wide table),
 * so permissions and validation stay in one place.
 *
 * Sections: dark header (name, section/category), status, progress
 * (declared % editable + computed hours read-only), finances, the
 * invoicing block, references, and manage actions.
 */

import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveIcon from '@mui/icons-material/UnarchiveOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';
import type { Designation, DesignationStatus, HourlyRate } from '@altyor-kronos/shared-types';
import { colors } from '../../core/theme/theme';
import { formatCurrencyEUR, formatNumber } from './finance';
import type { RateResolutionContext } from './rateResolver';
import RateCell from './RateCell';
import InvoicesBlock, { type InvoiceRow } from './InvoicesBlock';

// Status chip colors — designation statuses mapped onto the design
// guide's status palette (ordered=green, on-hold=yellow, rest=gray).
// Moved here from the list when the status column was dropped.
const STATUS_CHIP_SX: Record<DesignationStatus, { bgcolor: string; color: string }> = {
  pending: { bgcolor: colors.lightGray, color: colors.bodyText },
  ordered: { bgcolor: colors.softGreen, color: colors.bodyText },
  'on-hold': { bgcolor: colors.yellow, color: '#000000' },
  closed: { bgcolor: colors.lightGray, color: colors.textMuted },
};

/** Fields editable through the container's click-to-edit cells. */
export type EditableField =
  | 'initialOffer'
  | 'orderedAmount'
  | 'customerPO'
  | 'altyorPO'
  | 'subContracting'
  | 'status'
  | 'hoursSold'
  | 'provision'
  | 'comment'
  | 'progress';

export type RenderCell = (
  d: Designation,
  field: EditableField,
  display: ReactNode,
  testId?: string,
) => ReactNode;

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 32 }}
    >
      <Typography variant="body2" sx={{ color: colors.textMuted, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ textAlign: 'right' }}>{children}</Box>
    </Stack>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        borderBottom: `1px solid ${colors.lightGray}`,
        pb: 0.5,
        mb: 1,
        mt: 2,
      }}
    >
      {children}
    </Typography>
  );
}

export default function DesignationDetailPanel({
  designation: d,
  locale,
  rules,
  rateContext,
  canManage,
  canArchive,
  canSeeInvoices,
  canManageInvoices,
  invoices,
  renderCell,
  onRateOverrideCommit,
  onClose,
  onEditHeader,
  onToggleArchive,
  onDelete,
  onAddInvoice,
  onEditInvoice,
  onDeleteInvoice,
}: {
  designation: Designation;
  locale: string;
  rules: readonly HourlyRate[];
  rateContext: RateResolutionContext;
  canManage: boolean;
  /** Field-level gate (direction + admin on top of the full editors). */
  canArchive: boolean;
  canSeeInvoices: boolean;
  canManageInvoices: boolean;
  invoices: readonly InvoiceRow[];
  renderCell: RenderCell;
  onRateOverrideCommit: (v: number | null) => void;
  onClose: () => void;
  onEditHeader: () => void;
  onToggleArchive: () => void;
  onDelete: () => void;
  onAddInvoice: () => void;
  onEditInvoice: (invoice: InvoiceRow) => void;
  onDeleteInvoice: (invoice: InvoiceRow) => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isHourly = d.billingMode !== 'fixed';

  const content = (
    <Box data-testid="finance-detail-panel">
      {/* Dark header — design guide "dark card treatment" for emphasis. */}
      <Box sx={{ bgcolor: colors.darkBg, p: 2 }}>
        <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1 }}>
          <Typography
            variant="h4"
            sx={{
              color: colors.white,
              fontWeight: 700,
              flex: 1,
              textDecoration: d.isArchived ? 'line-through' : 'none',
            }}
          >
            {d.name}
          </Typography>
          {d.isArchived && (
            <Chip
              size="small"
              label={t('finance.archive.badge')}
              sx={{ bgcolor: colors.lightGray, color: colors.bodyText }}
            />
          )}
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t('finance.detail.close')}
            sx={{ color: colors.white }}
            data-testid="finance-detail-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="caption" sx={{ color: colors.lightGray }}>
          {t(`projects.designations.sections.${d.section}`)}
          {d.category ? ` · ${d.category}` : ''}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        <FieldRow label={t('finance.columns.status')}>
          {renderCell(
            d,
            'status',
            <Chip
              size="small"
              label={t(`finance.status.${d.status}`)}
              sx={STATUS_CHIP_SX[d.status] ?? STATUS_CHIP_SX.pending}
            />,
          )}
        </FieldRow>

        {/* ── Progress ── */}
        <SectionLabel>{t('finance.detail.progress')}</SectionLabel>
        <Stack spacing={0.75}>
          <FieldRow label={t('finance.detail.declared')}>
            {renderCell(
              d,
              'progress',
              d.progress != null ? `${formatNumber(d.progress, locale)}%` : '—',
              `finance-cell-progress-panel-${d.id}`,
            )}
          </FieldRow>
          <LinearProgress
            variant="determinate"
            value={Math.min(d.progress ?? 0, 100)}
            sx={{
              height: 6,
              backgroundColor: colors.lightGray,
              '& .MuiLinearProgress-bar': { backgroundColor: colors.yellow },
            }}
          />
          {isHourly && (
            <>
              <FieldRow label={t('finance.detail.hours')}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    component="span"
                    variant="body2"
                    data-testid={`finance-hoursSpent-${d.id}`}
                  >
                    {formatNumber(d.hoursSpent, locale)} / {formatNumber(d.hoursSold, locale)} h
                  </Typography>
                  <LockOutlinedIcon fontSize="inherit" sx={{ color: colors.textMuted }} />
                </Stack>
              </FieldRow>
              <FieldRow label={t('finance.detail.remainingHours')}>
                <Typography component="span" variant="body2">
                  {formatNumber(d.remainingHours, locale)}
                </Typography>
              </FieldRow>
            </>
          )}
        </Stack>

        {/* ── Finances ── */}
        <SectionLabel>{t('finance.detail.finances')}</SectionLabel>
        <Stack spacing={0.75}>
          <FieldRow label={t('finance.columns.initialOffer')}>
            {renderCell(d, 'initialOffer', formatCurrencyEUR(d.initialOffer ?? 0, locale))}
          </FieldRow>
          <FieldRow label={t('finance.columns.orderedAmount')}>
            {renderCell(d, 'orderedAmount', formatCurrencyEUR(d.orderedAmount ?? 0, locale))}
          </FieldRow>
          {d.section === 'rd' && (
            <>
              <FieldRow label={t('finance.columns.hourlyRate')}>
                <RateCell
                  designation={d}
                  rules={rules}
                  context={rateContext}
                  canManage={canManage}
                  onOverrideCommit={onRateOverrideCommit}
                />
              </FieldRow>
              <FieldRow label={t('finance.columns.hoursSold')}>
                {renderCell(d, 'hoursSold', formatNumber(d.hoursSold, locale))}
              </FieldRow>
            </>
          )}
          <FieldRow label={t('finance.columns.provision')}>
            {renderCell(d, 'provision', formatCurrencyEUR(d.provision ?? 0, locale))}
          </FieldRow>
        </Stack>

        {/* ── Invoicing ── */}
        {canSeeInvoices && (
          <Box sx={{ mt: 2 }}>
            <InvoicesBlock
              designation={d}
              invoices={invoices}
              locale={locale}
              canManageInvoices={canManageInvoices}
              onAdd={onAddInvoice}
              onEdit={onEditInvoice}
              onDelete={onDeleteInvoice}
            />
          </Box>
        )}

        {/* ── References ── */}
        <SectionLabel>{t('finance.detail.references')}</SectionLabel>
        <Stack spacing={0.75}>
          <FieldRow label={t('finance.columns.customerPO')}>
            {renderCell(d, 'customerPO', d.customerPO ?? '—')}
          </FieldRow>
          <FieldRow label={t('finance.columns.altyorPO')}>
            {renderCell(d, 'altyorPO', d.altyorPO ?? '—')}
          </FieldRow>
          <FieldRow label={t('finance.columns.subContracting')}>
            {renderCell(
              d,
              'subContracting',
              d.subContracting ? t('common.yes') : t('common.no'),
            )}
          </FieldRow>
          <FieldRow label={t('finance.columns.comment')}>
            {renderCell(d, 'comment', d.comment ?? '—')}
          </FieldRow>
        </Stack>

        {/* ── Actions ── */}
        {(canManage || canArchive) && (
          <Stack direction="row" spacing={1} sx={{ mt: 2.5, flexWrap: 'wrap', gap: 1 }}>
            {canManage && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<EditIcon fontSize="small" />}
                onClick={onEditHeader}
                data-testid={`finance-edit-${d.id}`}
              >
                {t('finance.detail.edit')}
              </Button>
            )}
            {canArchive && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={
                  d.isArchived ? (
                    <UnarchiveIcon fontSize="small" />
                  ) : (
                    <ArchiveIcon fontSize="small" />
                  )
                }
                onClick={onToggleArchive}
                data-testid={`finance-archive-${d.id}`}
              >
                {d.isArchived ? t('finance.archive.restore') : t('finance.archive.archive')}
              </Button>
            )}
            {canManage && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<DeleteIcon fontSize="small" />}
              onClick={onDelete}
              data-testid={`finance-delete-${d.id}`}
            >
              {t('common.delete')}
            </Button>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="right"
        open
        onClose={onClose}
        slotProps={{ paper: { sx: { width: '100%', maxWidth: 420 } } }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        width: 380,
        flexShrink: 0,
        alignSelf: 'flex-start',
        borderColor: colors.lightGray,
        overflow: 'hidden',
      }}
    >
      {content}
    </Paper>
  );
}
